import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Modal } from "react-bootstrap";

import Currency from "../shared/Currency";
import { url as appUrl } from "../../services/utils";
import { getSmartProductSuggestions } from "./smartProductSearch";

function AdvancedProductSearchModal({ show, initialQuery, category, onHide, onQueryChange, onViewAll }) {
  const [query, setQuery] = useState(initialQuery || "");
  const [products, setProducts] = useState([]);
  const [status, setStatus] = useState("idle");
  const [effectiveQuery, setEffectiveQuery] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (!show) return;
    setQuery(initialQuery || "");
    setTimeout(() => inputRef.current && inputRef.current.focus(), 150);
  }, [show, initialQuery]);

  useEffect(() => {
    if (!show) return undefined;

    const keyword = query.trim();
    let canceled = false;

    if (keyword.length < 2) {
      setProducts([]);
      setStatus("idle");
      setEffectiveQuery("");
      return undefined;
    }

    setStatus("loading");
    const timer = setTimeout(() => {
      getSmartProductSuggestions({ query: keyword, category, limit: 6 }).then(({ products: matches, effectiveQuery: candidate }) => {
          if (canceled) return;
          setProducts(matches);
          setEffectiveQuery(candidate);
          setStatus("success");
        }).catch(() => {
          if (canceled) return;
          setProducts([]);
          setEffectiveQuery("");
          setStatus("error");
        });
    }, 500);

    return () => {
      canceled = true;
      clearTimeout(timer);
    };
  }, [show, query, category]);

  const handleChange = (event) => {
    const value = event.target.value;
    setQuery(value);
    setEffectiveQuery("");
    if (onQueryChange) onQueryChange(value);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (query.trim() && onViewAll) onViewAll(effectiveQuery || query.trim());
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg" className="advanced-search-modal">
      <Modal.Header closeButton>
        <div className="advanced-search-modal__heading">
          <span className="advanced-search-modal__eyebrow">Package finder</span>
          <Modal.Title>Search UAE business packages</Modal.Title>
          <p>Search by activity, industry, licence type or package name.</p>
        </div>
      </Modal.Header>
      <Modal.Body>
        <form className="advanced-search-modal__form" onSubmit={handleSubmit}>
          <i className="fas fa-search" aria-hidden="true" />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={handleChange}
            placeholder="Try ‘E-commerce’, ‘pharma’ or ‘trading’"
            aria-label="Search business packages"
            autoComplete="off"
          />
          {query && (
            <button type="button" className="advanced-search-modal__clear" onClick={() => handleChange({ target: { value: "" } })} aria-label="Clear search">
              <i className="fas fa-times" aria-hidden="true" />
            </button>
          )}
          <button type="submit" className="advanced-search-modal__submit" disabled={!query.trim()}>
            Search
          </button>
        </form>

        <div className="advanced-search-modal__results" aria-live="polite">
          {status === "idle" && (
            <div className="advanced-search-modal__empty">
              <i className="fas fa-layer-group" aria-hidden="true" />
              <strong>Find the right package faster</strong>
              <span>Enter at least 2 characters to see matching packages.</span>
            </div>
          )}

          {status === "loading" && (
            <div className="advanced-search-modal__empty">
              <span className="spinner-border" aria-hidden="true" />
              <strong>Finding matching packages...</strong>
            </div>
          )}

          {status === "error" && (
            <div className="advanced-search-modal__empty advanced-search-modal__empty--error">
              <i className="fas fa-exclamation-circle" aria-hidden="true" />
              <strong>Suggestions are temporarily unavailable</strong>
              <span>You can still press Search to view the full results.</span>
            </div>
          )}

          {status === "success" && products.length === 0 && (
            <div className="advanced-search-modal__empty">
              <i className="far fa-folder-open" aria-hidden="true" />
              <strong>No matching packages found</strong>
              <span>Try a broader business activity or industry keyword.</span>
            </div>
          )}

          {status === "success" && products.length > 0 && (
            <>
              <div className="advanced-search-modal__result-heading">
                <strong>
                  Suggested packages
                  {effectiveQuery && effectiveQuery.toLowerCase() !== query.trim().toLowerCase() && (
                    <small> for “{effectiveQuery}”</small>
                  )}
                </strong>
                <span>{products.length} results</span>
              </div>
              <ul className="advanced-search-modal__list">
                {products.map((product) => (
                  <li key={product._id}>
                    <Link to={appUrl.product(product)} onClick={onHide} className="advanced-search-result">
                      <span className="advanced-search-result__image">
                        {product.images && product.images[0] ? (
                          <img src={product.images[0].url} alt="" />
                        ) : (
                          <i className="fas fa-building" aria-hidden="true" />
                        )}
                      </span>
                      <span className="advanced-search-result__info">
                        <strong>{product.title}</strong>
                        <small>{product.sku ? `SKU: ${product.sku}` : "UAE business package"}</small>
                      </span>
                      <span className="advanced-search-result__price">
                        <small>Starting from</small>
                        <strong><Currency value={product.offerPrice || product.salePrice || 0} /></strong>
                      </span>
                      <i className="fas fa-chevron-right advanced-search-result__arrow" aria-hidden="true" />
                    </Link>
                  </li>
                ))}
              </ul>
              <button type="button" className="advanced-search-modal__view-all" onClick={() => onViewAll(effectiveQuery || query.trim())}>
                View all matching packages
                <i className="fas fa-arrow-right" aria-hidden="true" />
              </button>
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default AdvancedProductSearchModal;
